import type { PlayerData } from "../types";
import { formatPlayerName } from "./nameFormat";
import { apiClient } from "./api";

type PlayerDataLoose = Omit<
  Partial<PlayerData>,
  | "country_name"
  | "player_height"
  | "player_weight"
  | "height"
  | "weight"
  | "nationality"
>;

export interface PlayerApiResponse extends PlayerDataLoose {
  player_id: string;
  name: string;
  birth_date: string;
  player_height?: number | null;
  player_weight?: number | null;
  country_name?: string | null;
  height?: number | null;
  weight?: number | null;
  nationality?: string | null;
}

// Normalize backend player payload to the shape expected by PlayerData consumers.
export function normalizePlayer(player: PlayerApiResponse): PlayerData {
  const {
    player_height,
    player_weight,
    country_name,
    birth_date,
    height,
    weight,
    nationality,
    ...rest
  } = player;

  const trimmedBirthDate = birth_date ? birth_date.split("T")[0] : "";

  return {
    ...rest,
    player_id: player.player_id,
    name: formatPlayerName(player.name),
    birth_date: trimmedBirthDate,
    player_height: player_height ?? height ?? undefined,
    player_weight: player_weight ?? weight ?? undefined,
    country_name: country_name ?? nationality ?? "",
  } as PlayerData;
}

export function normalizePlayers(players: PlayerApiResponse[]): PlayerData[] {
  return players.map(normalizePlayer);
}

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toIsoDate = (value?: string) =>
  value && value.trim().length > 0
    ? new Date(`${value}T00:00:00Z`).toISOString()
    : undefined;

const sanitizeI18nNames = (
  names?: Record<string, string | undefined | null>,
) => {
  if (!names) return undefined;
  const normalized = Object.entries(names)
    .map(([locale, value]) => [locale, (value ?? "").trim()])
    .filter(([, value]) => Boolean(value));
  return normalized.length > 0 ? Object.fromEntries(normalized) : undefined;
};

/**
 * Build backend-compliant payload for player create/update/change-detection flows.
 *
 * `originalName` (EPIC-95 T4): pass the player's name AS LOADED into the
 * form when editing an existing player. When the admin never touched the
 * name field, `data.name` is still exactly that same string — so `name` is
 * omitted from the payload entirely rather than reformatted and resent.
 * Without this, saving any OTHER field (e.g. filling in a missing birth
 * date) would silently re-run the untouched name through
 * `formatPlayerName` and overwrite the stored value — which would undo the
 * roster importer's own particle-casing normalization ("Johan van Dijk")
 * the first time an admin opens an imported player and saves any change.
 * Omitted entirely (not passed) on a CREATE, where there is no original to
 * compare against — the name is always formatted there, as before.
 */
export const buildPlayerPayload = (
  data: Partial<PlayerData>,
  options: { originalName?: string } = {},
) => {
  const trimmedName = data.name?.trim();
  const nameUnchanged =
    options.originalName !== undefined &&
    trimmedName === options.originalName.trim();
  const normalizedName = nameUnchanged
    ? undefined
    : formatPlayerName(trimmedName);
  const payload = {
    player_id: data.player_id?.trim() || undefined,
    name: normalizedName || undefined,
    nickname: data.nickname?.trim() || undefined,
    birth_date: toIsoDate(data.birth_date),
    player_height: toNumberOrUndefined(
      (data as any).height ?? data.player_height,
    ),
    player_weight: toNumberOrUndefined(
      (data as any).weight ?? data.player_weight,
    ),
    country_name:
      (data as any).nationality?.trim() ?? data.country_name?.trim(),
    position: data.position,
    i18n_names: sanitizeI18nNames(data.i18n_names),
  } as Record<string, unknown>;

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );
};

// ── Missing-fields / inline completion (EPIC-95, AC7) ───────────────────────

/**
 * The three player-level fields the backend's `update_player` handler
 * checks before auto-clearing `is_provisional`
 * (ProMatchAnalytics-Backend/app/routers/players.py: `all(merged_player.get(
 * field) for field in ("birth_date", "country_name", "position"))`).
 * Kept as its own list (not derived from `PlayerData`'s keys) so the set
 * stays exactly in sync with that backend rule rather than drifting toward
 * "every optional PlayerData field".
 */
export const PLAYER_COMPLETENESS_FIELDS = [
  "birth_date",
  "country_name",
  "position",
] as const;

export type PlayerMissingField = (typeof PLAYER_COMPLETENESS_FIELDS)[number];

/** Which of the three completeness fields are empty on this player, in a
 *  stable display order. A roster-import row can land with a blank
 *  `position` (the extract schema marks it optional) even though the
 *  hand-authored create/edit form always defaults one — so this checks all
 *  three regardless of `is_provisional`, rather than trusting that flag as
 *  a proxy for "nothing is missing". */
export function computeMissingPlayerFields(
  player: Pick<PlayerData, "birth_date" | "country_name" | "position">,
): PlayerMissingField[] {
  return PLAYER_COMPLETENESS_FIELDS.filter((field) => !player[field]?.trim?.());
}

/** Whether `player` has at least one active team link with no jersey
 *  number yet (`GET /players/`'s batched `missing_jersey_teams` — see
 *  `PlayerMissingJerseyTeam` in `../types`). A player can clear every
 *  `computeMissingPlayerFields` entry and still have this be true: a jersey
 *  gap never gates `is_provisional` (EPIC-95 judge round 1, point 3 —
 *  the spec's amended Completeness paragraph), so the players view must
 *  check the two independently rather than folding this into that list. */
export function hasMissingTeamJersey(
  player: Pick<PlayerData, "missing_jersey_teams">,
): boolean {
  return (player.missing_jersey_teams?.length ?? 0) > 0;
}

/** One team roster assignment for a player, as returned by
 *  `GET /players/{player_id}/teams` (app/routers/players.py `get_player_teams`).
 *  `jersey_number` is `null` once a team link is created without one
 *  (EPIC-95 T5, "Jersey decision" in team_plan.md: `TeamPlayerBase.
 *  jersey_number` is now `Optional[int]`) — the roster importer's own
 *  create/link apply no longer requires it. */
export interface PlayerTeamAssignment {
  team_id: string;
  team_name: string;
  jersey_number: number | null;
  position: string;
  is_active: boolean;
  joined_date?: string | null;
}

export const fetchPlayerTeamAssignments = async (
  playerId: string,
): Promise<PlayerTeamAssignment[]> =>
  apiClient.get<PlayerTeamAssignment[]>(`/players/${playerId}/teams`);

/** A jersey number of null/0/undefined is never a real shirt (the app's own
 *  jersey inputs mirror the ingestion prompt's "1-99" range) — treated as
 *  "not yet set", which is now the backend's own real, first-class shape
 *  (EPIC-95 T5: `jersey_number` is `Optional[int]` on a team link) rather
 *  than a client-side convention alone. */
export const isJerseyNumberMissing = (
  assignment: Pick<PlayerTeamAssignment, "jersey_number">,
): boolean => !assignment.jersey_number || assignment.jersey_number <= 0;

/** Renders a jersey number for display, "—" when missing (same rule as
 *  `isJerseyNumberMissing` above) — never a bare `#` (React drops a `null`/
 *  `undefined` child silently) or a fabricated `0`. */
export const formatJerseyNumberDisplay = (
  jerseyNumber: number | null | undefined,
): string => (jerseyNumber && jerseyNumber > 0 ? String(jerseyNumber) : "—");

/** PUT /teams/{teamId}/players/{playerId} — same endpoint TeamsManager's
 *  roster editor uses; here scoped to just the jersey number. */
export const updateTeamPlayerJerseyNumber = async (
  teamId: string,
  playerId: string,
  jerseyNumber: number,
): Promise<void> => {
  await apiClient.put(`/teams/${teamId}/players/${playerId}`, {
    jersey_number: jerseyNumber,
  });
};

/** Build a `PUT /players/{id}` payload containing only the completeness
 *  fields the admin just filled in (inline "complete profile" — not a full
 *  form resubmission). `toIsoDate` mirrors `buildPlayerPayload`'s own
 *  date handling so a bare `YYYY-MM-DD` input becomes the same
 *  UTC-midnight ISO string the backend already expects. */
export const buildPlayerCompletionPayload = (fields: {
  birth_date?: string;
  country_name?: string;
  position?: string;
}): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  if (fields.birth_date?.trim()) {
    payload.birth_date = toIsoDate(fields.birth_date);
  }
  if (fields.country_name?.trim()) {
    payload.country_name = fields.country_name.trim();
  }
  if (fields.position?.trim()) {
    payload.position = fields.position;
  }
  return payload;
};
